<template>
  <Modal
    :is-open="isOpen"
    icon="arrow-right-from-arc"
    title="Transfer"
    description="Transfer this agreement into another dealership."
    :buttons="['close', 'ok']"
    close-text="Cancel"
    ok-text="Transfer"
    min-width="500"
    max-width="500"
    :is-confirming="isLoading"
    :disable-close="isLoading"
    @close-modal="$emit('close')"
    @ok-modal="$emit('save')"
  >
    <Alert
      severity="error"
      title="The dealership will have access to customers contact details! Triple check before transferring."
      :show-dismiss-button="false"
      :show-close-button="false"
      class="mb-4"
    />
    <FormGroup label="Transfer into">
      <Select
        :model-value="selectedDealershipID ?? undefined"
        :options="dealershipsList"
        value-key="value"
        label-key="label"
        searchable
        :has-error="dealershipHasError"
        :handle-search="true"
        @update:model-value="$emit('update:selected-dealership-id', $event)"
      >
        <template #option="slotProps">
          <div class="flex gap-2">
            <Image
              :src="slotProps.option.brandLogo"
              class="w-5"
            />
            <div>{{ slotProps.option.label }}</div>
          </div>
        </template>
      </Select>
      <InputError :has-error="dealershipHasError">{{ dealershipErrorMessage }}</InputError>
    </FormGroup>

    <FormGroup :label="$t('ui.common.note')">
      <TextArea
        :model-value="note ?? undefined"
        rows="3"
        auto-resize
        class="w-full"
        style="max-height: 150px !important"
        placeholder="Add a reason..."
        has-speech-to-text
        :has-error="noteHasError"
        @update:model-value="$emit('update:note', $event)"
      />
      <InputError :has-error="noteHasError">{{ noteErrorMessage }}</InputError>
    </FormGroup>

    <FormGroup label="Transferred by">
      <Chip
        :avatar="userProfilePicUrl ?? undefined"
        :avatar-text="userInitials ?? undefined"
      >
        {{ userName }}
      </Chip>
    </FormGroup>
  </Modal>
</template>

<script setup lang="ts">
// components
import Modal from '@/components/modal/Modal.vue'
import Alert from '@/components/alert/Alert.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Select from '@/components/forms/Select.vue'
import TextArea from '@/components/forms/TextArea.vue'
import InputError from '@/components/forms/InputError.vue'
import Chip from '@/components/chip/Chip.vue'
import Image from '@/components/image/Image.vue'
// types
import type { DealershipOption } from '@/types/domain/dealership'

interface Props {
  isOpen: boolean
  isLoading: boolean
  selectedDealershipID: number | null
  dealershipsList: DealershipOption[]
  note: string | null
  dealershipHasError: boolean
  dealershipErrorMessage: string | null
  noteHasError: boolean
  noteErrorMessage: string | null
  userProfilePicUrl: string | null
  userInitials: string | null
  userName: string | null
}

defineProps<Props>()

defineEmits<{
  close: []
  save: []
  'update:selected-dealership-id': [id: number | null]
  'update:note': [note: string | null]
}>()
</script>
